import * as React from 'react';

import { cn } from '../lib/cn';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

export interface CountryCode {
  iso: string;
  name: string;
  dial: string;
  flag: string;
}

export const DEFAULT_COUNTRY_CODES: CountryCode[] = [
  { iso: 'CZ', name: 'Czechia', dial: '+420', flag: '🇨🇿' },
  { iso: 'SK', name: 'Slovakia', dial: '+421', flag: '🇸🇰' },
  { iso: 'AT', name: 'Austria', dial: '+43', flag: '🇦🇹' },
  { iso: 'DE', name: 'Germany', dial: '+49', flag: '🇩🇪' },
  { iso: 'PL', name: 'Poland', dial: '+48', flag: '🇵🇱' },
  { iso: 'HU', name: 'Hungary', dial: '+36', flag: '🇭🇺' },
  { iso: 'GB', name: 'United Kingdom', dial: '+44', flag: '🇬🇧' },
  { iso: 'FR', name: 'France', dial: '+33', flag: '🇫🇷' },
  { iso: 'IT', name: 'Italy', dial: '+39', flag: '🇮🇹' },
  { iso: 'ES', name: 'Spain', dial: '+34', flag: '🇪🇸' },
  { iso: 'NL', name: 'Netherlands', dial: '+31', flag: '🇳🇱' },
  { iso: 'CH', name: 'Switzerland', dial: '+41', flag: '🇨🇭' },
  { iso: 'US', name: 'United States', dial: '+1', flag: '🇺🇸' },
];

export interface PhoneValue {
  country: string; // ISO code
  number: string; // national number
}

export interface PhoneInputProps {
  value?: PhoneValue;
  onChange?: (v: PhoneValue) => void;
  countries?: CountryCode[];
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
}

export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      value = { country: 'CZ', number: '' },
      onChange,
      countries = DEFAULT_COUNTRY_CODES,
      invalid,
      disabled,
      placeholder = '777 123 456',
      className,
      id,
    },
    ref,
  ) => {
    const current = countries.find((c) => c.iso === value.country) ?? countries[0];
    return (
      <div
        className={cn(
          'flex h-9 items-center gap-1 rounded-[var(--radius-md)] border bg-[var(--color-surface)] pl-1 pr-3',
          'focus-within:ring-2 focus-within:ring-[var(--color-ring)]',
          invalid ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]',
          disabled && 'cursor-not-allowed opacity-60',
          className,
        )}
      >
        <Select
          value={value.country}
          onValueChange={(c) => onChange?.({ ...value, country: c })}
          disabled={disabled}
        >
          <SelectTrigger className="h-7 w-auto gap-1 border-0 bg-transparent px-2 text-sm focus-visible:ring-0">
            <SelectValue>
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden>{current.flag}</span>
                <span>{current.dial}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {countries.map((c) => (
              <SelectItem key={c.iso} value={c.iso}>
                <span className="inline-flex items-center gap-2">
                  <span aria-hidden>{c.flag}</span>
                  <span>{c.name}</span>
                  <span className="text-[var(--color-fg-muted)]">{c.dial}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input
          ref={ref}
          id={id}
          type="tel"
          inputMode="tel"
          disabled={disabled}
          value={value.number}
          onChange={(e) => onChange?.({ ...value, number: e.target.value })}
          placeholder={placeholder}
          aria-invalid={invalid || undefined}
          className="h-full flex-1 bg-transparent text-sm text-[var(--color-fg)] outline-none placeholder:text-[var(--color-fg-muted)]"
        />
      </div>
    );
  },
);
PhoneInput.displayName = 'PhoneInput';
